import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormMode } from '../contexts/FormModeContext';
import { useValidationErrors } from '../contexts/ValidationErrorsContext';
import { useFormSave } from '../hooks/useFormSave';
import { useIPSync } from '../hooks/useIPSync';
import { ApplicationForm } from '../types';
import {
  checkAllSectionsCompletion,
  checkBasicInfoCompletion,
  checkBudgetCompletion,
  checkContentCompletion,
  checkPublicationsCompletion,
  checkTeamInfoCompletion,
  getBasicInfoMissingFields,
  getTeamInfoMissingFields,
} from '../utils/sectionCompletion';
import { isConsolidated } from '../utils/classificationUtils';
import styles from './ReviewSubmission.module.css';
import SingaporeDeclarationModal from './SingaporeDeclarationModal';
import ValidationErrorDisplay from './ValidationErrorDisplay';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import { combineClasses } from '../utils/combineClasses';
import { identifierTypeLabel } from '../utils/publications';

interface ReviewSubmissionProps {
  readOnly?: boolean;
  isActive?: boolean;
}

const ReviewSubmission: React.FC<ReviewSubmissionProps> = ({
  readOnly = false,
  isActive = true,
}) => {
  const [singaporeOpen, setSingaporeOpen] = useState(false);

  // Initialize form save hook for context awareness
  const { setUpdating } = useFormSave({ stepId: 'review' });

  // Ensure IP synchronization runs when reviewing
  useIPSync(setUpdating);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ApplicationForm>();

  const { validationErrors, clearValidationErrors } = useValidationErrors();
  const { createdAt, submittedAt } = useFormMode();

  const formData = watch();

  // Helper function to format dates
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return null;
    }
  };

  // Watch for mutual exclusivity between incompatibilities and "no incompatibilities"
  const noIncompatibilities = watch('noIncompatibilities');
  const finalReportsUC = watch('finalReportsUC');
  const finalReportsANID = watch('finalReportsANID');
  const duplicateFunding = watch('duplicateFunding');
  const otherIncompatibilities = watch('otherIncompatibilities');

  const prevNoIncompatibilities = React.useRef(noIncompatibilities);
  const prevIncompatibilities = React.useRef({
    finalReportsUC,
    finalReportsANID,
    duplicateFunding,
    otherIncompatibilities,
  });

  React.useEffect(() => {
    // Check if "No tengo incompatibilidades" was just checked (changed from false to true)
    if (noIncompatibilities && !prevNoIncompatibilities.current) {
      setValue('finalReportsUC', false);
      setValue('finalReportsANID', false);
      setValue('duplicateFunding', false);
      setValue('otherIncompatibilities', false);
      setValue('otherIncompatibilitiesDetails', '');
    }
    // Check if any incompatibility was just checked and "No tengo incompatibilidades" is currently true
    else if (
      noIncompatibilities &&
      ((finalReportsUC && !prevIncompatibilities.current.finalReportsUC) ||
        (finalReportsANID && !prevIncompatibilities.current.finalReportsANID) ||
        (duplicateFunding && !prevIncompatibilities.current.duplicateFunding) ||
        (otherIncompatibilities &&
          !prevIncompatibilities.current.otherIncompatibilities))
    ) {
      setValue('noIncompatibilities', false);
    }

    // Update refs for next render
    prevNoIncompatibilities.current = noIncompatibilities;
    prevIncompatibilities.current = {
      finalReportsUC,
      finalReportsANID,
      duplicateFunding,
      otherIncompatibilities,
    };
  }, [
    noIncompatibilities,
    finalReportsUC,
    finalReportsANID,
    duplicateFunding,
    otherIncompatibilities,
    setValue,
  ]);

  // Clear validation errors when user makes actual changes to form fields
  const formDataRef = React.useRef(formData);
  React.useEffect(() => {
    // Only clear if we have validation errors and form data actually changed
    if (validationErrors.length > 0) {
      const currentFormData = JSON.stringify(formData);
      const previousFormData = JSON.stringify(formDataRef.current);

      if (currentFormData !== previousFormData) {
        clearValidationErrors();
      }
    }
    formDataRef.current = formData;
  }, [formData, validationErrors.length, clearValidationErrors]);

  // Check completion status for each section using utility functions
  const basicInfoComplete = checkBasicInfoCompletion(formData);
  const basicInfoMissingFields = getBasicInfoMissingFields(formData);
  const teamInfoComplete = checkTeamInfoCompletion(formData);
  const teamInfoMissingFields = getTeamInfoMissingFields(formData);
  const contentComplete = checkContentCompletion(formData);
  const budgetComplete = checkBudgetCompletion(formData);
  const publicationsComplete = checkPublicationsCompletion(formData);
  const allSectionsComplete = checkAllSectionsCompletion(formData);

  const getTeamType = () => {
    const irIsConsolidated = isConsolidated(
      formData.principalInvestigator?.doctorateYear,
      formData.principalInvestigator?.doctorateMonth
    );

    const ipIsConsolidated = formData.coInvestigators
      ?.filter((c: any) => c.role === 'IP')
      .some((c: any) => isConsolidated(c.doctorateYear, c.doctorateMonth));

    return irIsConsolidated || ipIsConsolidated
      ? 'Consolidado'
      : 'Emergente';
  };

  return (
    <>
    <div>
      <h2 className={styles.sectionTitle}>Envío</h2>

      <div className={styles.submissionPreview}>
        <h3 className={styles.previewTitle}>Revisión de la postulación</h3>
        <div className={styles.previewGrid}>
          <div className={styles.previewLabel}>Título:</div>
          <div className={styles.previewValue}>
            {formData.projectTitle || 'No especificado'}
          </div>

          <div className={styles.previewLabel}>Investigador Responsable:</div>
          <div className={styles.previewValue}>
            {formData.principalInvestigator?.name || 'No especificado'}
          </div>

          <div className={styles.previewLabel}>Línea de investigación:</div>
          <div className={styles.previewValue}>
            {formData.evaluationArea || 'No especificada'}
          </div>

          <div className={styles.previewLabel}>Categoría del proyecto:</div>
          <div className={styles.previewValue}>
            {getTeamType() === 'Consolidado'
              ? 'Categoría B — Consolidado (compite con proyectos consolidados)'
              : 'Categoría A — Emergente (compite con proyectos emergentes)'}
          </div>

          <div className={styles.previewLabel}>Integrantes del equipo:</div>
          <div className={styles.previewValue}>
            {(() => {
              const ipCount =
                formData.coInvestigators?.filter((ci) => ci.role === 'IP').length || 0;
              const iaCount =
                formData.coInvestigators?.filter((ci) => ci.role === 'IA').length || 0;
              const coiCount =
                formData.coInvestigators?.filter((ci) => ci.role === 'COI').length || 0;
              const total = 1 + ipCount + iaCount + coiCount;
              return `${total} (IR: 1, IP: ${ipCount}, IA: ${iaCount}, COI: ${coiCount})`;
            })()}
          </div>

          <div className={styles.previewLabel}>Publicaciones por IP:</div>
          <div className={styles.previewValue}>
            {formData.ipCvData?.length > 0
              ? formData.ipCvData
                  .map(
                    (cvData: any) =>
                      `${cvData.investigatorName}: ${
                        cvData.publications?.length || 0
                      }/5 máximo`
                  )
                  .join(', ')
              : 'No hay datos de IP'}
          </div>

          {createdAt && (
            <>
              <div className={styles.previewLabel}>Fecha de Creación:</div>
              <div className={styles.previewValue}>
                {formatDate(createdAt) || 'No disponible'}
              </div>
            </>
          )}

          {submittedAt && (
            <>
              <div className={styles.previewLabel}>Fecha de Envío:</div>
              <div className={styles.previewValue}>
                {formatDate(submittedAt) || 'No disponible'}
              </div>
            </>
          )}
        </div>
      </div>

      <section className={styles.section}>
        <h3 className={styles.subTitle}>Estado de Completitud por Sección</h3>

        <div className={styles.reviewGrid}>
          <div
            className={combineClasses(
              styles.reviewCard,
              basicInfoComplete ? styles.complete : styles.incomplete
            )}
          >
            <h3 className={styles.cardTitle}>
              <span
                className={combineClasses(
                  styles.statusIcon,
                  basicInfoComplete ? styles.complete : styles.incomplete
                )}
              >
                {basicInfoComplete ? '✓' : '⚠'}
              </span>
              Antecedentes
            </h3>
            <ul className={styles.requirementsList}>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  basicInfoComplete ? styles.fulfilled : styles.unfulfilled
                )}
              >
                <div style={{ width: '100%' }}>
                  Información del proyecto
                  {basicInfoMissingFields.some(
                    (field) => !field.isComplete
                  ) && (
                    <ul
                      style={{
                        marginTop: '0.5rem',
                        marginLeft: '0.5rem',
                        listStyle: 'none',
                        padding: 0,
                      }}
                    >
                      {basicInfoMissingFields.map((field, index) => (
                        <li key={index} style={{ marginBottom: '0.25rem' }}>
                          <div
                            className={combineClasses(
                              styles.requirementItem,
                              field.isComplete
                                ? styles.fulfilled
                                : styles.unfulfilled
                            )}
                            style={{
                              fontSize: '0.8rem',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {field.name}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            </ul>
          </div>

          <div
            className={combineClasses(
              styles.reviewCard,
              contentComplete ? styles.complete : styles.incomplete
            )}
          >
            <h3 className={styles.cardTitle}>
              <span
                className={combineClasses(
                  styles.statusIcon,
                  contentComplete ? styles.complete : styles.incomplete
                )}
              >
                {contentComplete ? '✓' : '⚠'}
              </span>
              Formulación
            </h3>
            <ul className={styles.requirementsList}>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  !!formData.executiveSummary
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Resumen ejecutivo
              </li>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  !!formData.projectDocument
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Documento del proyecto (PDF)
              </li>
            </ul>
          </div>

          <div
            className={combineClasses(
              styles.reviewCard,
              teamInfoComplete ? styles.complete : styles.incomplete
            )}
          >
            <h3 className={styles.cardTitle}>
              <span
                className={combineClasses(
                  styles.statusIcon,
                  teamInfoComplete ? styles.complete : styles.incomplete
                )}
              >
                {teamInfoComplete ? '✓' : '⚠'}
              </span>
              Equipo de Trabajo
            </h3>
            <ul className={styles.requirementsList}>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  // Check if all existing co-investigators are complete
                  !formData.coInvestigators ||
                    formData.coInvestigators.length === 0 ||
                    formData.coInvestigators.every((ci: any) => {
                      // RUN is only required when documentType is 'run' (or undefined for backwards compatibility)
                      const runRequired =
                        !ci.documentType || ci.documentType === 'run';
                      const runComplete = runRequired ? !!ci.run : true;
                      const basicFields = ci.name && ci.email && runComplete;
                      if (ci.role === 'IP') {
                        return (
                          basicFields &&
                          ci.doctorateYear &&
                          ci.affiliation &&
                          ci.facultadUC &&
                          ci.unidadAcademicaUC
                        );
                      } else {
                        return (
                          basicFields && ci.affiliation && ci.institutionType
                        );
                      }
                    })
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Integrantes del equipo (
                {1 + (formData.coInvestigators?.length || 0)}/4)
              </li>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  !!formData.principalInvestigator?.name
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Datos del investigador responsable
              </li>
              {(() => {
                const hasIntCOI = formData.coInvestigators?.some(
                  (ci: any) => ci.role === 'COI' && ci.institutionType === 'Internacional'
                );
                if (hasIntCOI) return null;
                const justOk = !!(formData.internationalCoiJustification?.trim());
                return (
                  <li
                    className={combineClasses(
                      styles.requirementItem,
                      justOk ? styles.fulfilled : styles.unfulfilled
                    )}
                  >
                    Justificación ausencia Co-Investigador extranjero
                  </li>
                );
              })()}
              <li
                className={combineClasses(
                  styles.requirementItem,
                  teamInfoComplete ? styles.fulfilled : styles.unfulfilled
                )}
              >
                <div style={{ width: '100%' }}>
                  Información de todos los investigadores
                  {teamInfoMissingFields.length > 0 && (
                    <ul
                      style={{
                        marginTop: '0.5rem',
                        marginLeft: '0.5rem',
                        listStyle: 'none',
                        padding: 0,
                      }}
                    >
                      {teamInfoMissingFields.map((member, index) => (
                        <li
                          key={index}
                          style={{
                            marginBottom: '0.5rem',
                            fontSize: '0.85rem',
                            color: '#6c757d',
                          }}
                        >
                          <div style={{ marginBottom: '0.25rem' }}>
                            <div
                              className={combineClasses(
                                styles.requirementItem,
                                member.isComplete
                                  ? styles.fulfilled
                                  : styles.unfulfilled
                              )}
                              style={{
                                fontSize: '0.85rem',
                                marginBottom: '0.25rem',
                              }}
                            >
                              <strong>{member.name}</strong>
                            </div>
                          </div>
                          {!member.isComplete && (
                            <div style={{ marginLeft: '1rem' }}>
                              {member.fields.map((field, fieldIndex) => (
                                <div
                                  key={fieldIndex}
                                  className={combineClasses(
                                    styles.requirementItem,
                                    field.isComplete
                                      ? styles.fulfilled
                                      : styles.unfulfilled
                                  )}
                                  style={{
                                    fontSize: '0.8rem',
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  {field.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            </ul>
          </div>

          <div
            className={combineClasses(
              styles.reviewCard,
              budgetComplete ? styles.complete : styles.incomplete
            )}
          >
            <h3 className={styles.cardTitle}>
              <span
                className={combineClasses(
                  styles.statusIcon,
                  budgetComplete ? styles.complete : styles.incomplete
                )}
              >
                {budgetComplete ? '✓' : '⚠'}
              </span>
              Recursos
            </h3>
            <ul className={styles.requirementsList}>
              <li
                className={combineClasses(
                  styles.requirementItem,
                  !!formData.budget?.detailedFile
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Archivo de presupuesto detallado
              </li>
            </ul>
          </div>

          <div
            className={combineClasses(
              styles.reviewCard,
              publicationsComplete ? styles.complete : styles.incomplete
            )}
          >
            <h3 className={styles.cardTitle}>
              <span
                className={combineClasses(
                  styles.statusIcon,
                  publicationsComplete ? styles.complete : styles.incomplete
                )}
              >
                {publicationsComplete ? '✓' : '⚠'}
              </span>
              Publicaciones y Anexos
            </h3>
            <ul className={styles.requirementsList}>
              {/* CV y Publicaciones por Investigador Principal */}
              {formData.ipCvData?.map((cvData: any, index: number) => {
                const isIR = index === 0;
                const doiFile = isIR
                  ? formData.principalInvestigatorDoiFile
                  : formData.coInvestigatorDoiFile;

                return (
                  <div
                    key={index}
                    style={{
                      marginBottom: '1.5rem',
                      paddingLeft: '1rem',
                      borderLeft: '3px solid #dee2e6',
                    }}
                  >
                    <strong
                      style={{
                        color: 'var(--color-primary-dark)',
                        display: 'block',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {cvData.investigatorName}{' '}
                      {isIR
                        ? '(Investigador Responsable)'
                        : '(Investigador Principal)'}
                    </strong>
                    <li
                      className={combineClasses(
                        styles.requirementItem,
                        cvData.cvNarrative && cvData.cvNarrative.length > 0
                          ? styles.fulfilled
                          : styles.unfulfilled
                      )}
                    >
                      Narrativa del CV
                    </li>
                    <li
                      className={combineClasses(
                        styles.requirementItem,
                        cvData.publications?.length >= 0 &&
                          cvData.publications?.length <= 5 &&
                          cvData.publications.every(
                            // 2026-07-21: archivo adjunto obligatorio por publicación.
                            (pub: any) => !!pub.fileId
                          )
                          ? styles.fulfilled
                          : styles.unfulfilled
                      )}
                    >
                      <div style={{ width: '100%' }}>
                        Publicaciones ({cvData.publications?.length || 0}/5
                        máximo)
                        {cvData.publications &&
                          cvData.publications.length > 0 && (
                            <ul
                              style={{
                                marginTop: '0.5rem',
                                marginLeft: '0.5rem',
                                listStyle: 'none',
                                padding: 0,
                              }}
                            >
                              {cvData.publications.map(
                                (publication: any, pubIndex: number) => (
                                  <li
                                    key={pubIndex}
                                    style={{
                                      marginBottom: '0.5rem',
                                      fontSize: '0.85rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <div style={{ marginBottom: '0.25rem' }}>
                                      <strong>
                                        Publicación {pubIndex + 1}:
                                      </strong>{' '}
                                      {publication.title || ''}
                                    </div>
                                    <div style={{ marginLeft: '1rem' }}>
                                      <div
                                        className={combineClasses(
                                          styles.requirementItem,
                                          publication.fileId
                                            ? styles.fulfilled
                                            : styles.unfulfilled
                                        )}
                                        style={{
                                          fontSize: '0.8rem',
                                          marginBottom: '0.25rem',
                                        }}
                                      >
                                        {publication.fileId
                                          ? `Archivo adjunto${publication.identifierValue ? ` · ${identifierTypeLabel(publication.identifierType)}: ${publication.identifierValue}` : ''}`
                                          : 'Falta adjuntar el archivo'}
                                      </div>
                                    </div>
                                  </li>
                                )
                              )}
                            </ul>
                          )}
                      </div>
                    </li>
                    <li
                      className={combineClasses(
                        styles.requirementItem,
                        !!doiFile ? styles.fulfilled : styles.unfulfilled
                      )}
                    >
                      Declaración Obligatoria Interna (DOI)
                    </li>
                  </div>
                );
              }) || (
                <li
                  className={combineClasses(
                    styles.requirementItem,
                    styles.unfulfilled
                  )}
                >
                  No hay datos de investigadores principales
                </li>
              )}

              {/* Cartas de compromiso (solo si hay COI) */}
              {formData.coInvestigators?.filter((ci: any) => ci.role === 'COI')
                .length > 0 && (
                <div
                  style={{
                    marginBottom: '1.5rem',
                    paddingLeft: '1rem',
                    borderLeft: '3px solid #dee2e6',
                  }}
                >
                  <strong
                    style={{
                      color: 'var(--color-primary-dark)',
                      display: 'block',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Cartas de compromiso de Co-investigadores
                  </strong>
                  {formData.coInvestigators
                    ?.filter((ci: any) => ci.role === 'COI')
                    .map((coi: any, index: number) => (
                      <li
                        key={`coi-commitment-${index}`}
                        className={combineClasses(
                          styles.requirementItem,
                          !!coi.commitmentLetter
                            ? styles.fulfilled
                            : styles.unfulfilled
                        )}
                      >
                        {coi.name || `Co-investigador ${index + 1}`}
                      </li>
                    ))}
                </div>
              )}

              {/* Cartas de apoyo */}
              <div
                style={{
                  marginBottom: '1.5rem',
                  paddingLeft: '1rem',
                  borderLeft: '3px solid #dee2e6',
                }}
              >
                <strong
                  style={{
                    color: 'var(--color-primary-dark)',
                    display: 'block',
                    marginBottom: '0.5rem',
                  }}
                >
                  Cartas de apoyo
                </strong>
                <li
                  className={combineClasses(
                    styles.requirementItem,
                    !formData.institutionCommitmentLetters ||
                      formData.institutionCommitmentLetters.length === 0 ||
                      (formData.institutionCommitmentLetters.length >= 0 &&
                        formData.institutionCommitmentLetters.every(
                          (letter) => letter && letter.trim() !== ''
                        ))
                      ? styles.fulfilled
                      : styles.unfulfilled
                  )}
                >
                  Cartas de poyo (
                  {formData.institutionCommitmentLetters?.length ?? 0})
                </li>
                {formData.institutionCommitmentLetters &&
                  formData.institutionCommitmentLetters.length > 0 &&
                  formData.institutionCommitmentLetters.length >= 0 &&
                  !formData.institutionCommitmentLetters.every(
                    (letter) => letter && letter.trim() !== ''
                  ) && (
                    <li
                      className={combineClasses(
                        styles.requirementItem,
                        styles.unfulfilled
                      )}
                      style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                    >
                      {
                        formData.institutionCommitmentLetters.filter(
                          (letter) => !letter || letter.trim() === ''
                        ).length
                      }{' '}
                      carta(s) sin archivo adjunto
                    </li>
                  )}
              </div>

              {/* Evaluadores Sugeridos */}
              <li
                className={combineClasses(
                  styles.requirementItem,
                  formData.suggestedReviewers &&
                    formData.suggestedReviewers.length > 0
                    ? formData.suggestedReviewers.every(
                        (reviewer) => reviewer.name && reviewer.institution
                      )
                      ? styles.fulfilled
                      : styles.unfulfilled
                    : styles.fulfilled
                )}
              >
                Evaluadores sugeridos (
                {formData.suggestedReviewers?.length || 0})
              </li>
              {formData.suggestedReviewers &&
                formData.suggestedReviewers.length > 0 &&
                !formData.suggestedReviewers.every(
                  (reviewer) => reviewer.name && reviewer.institution
                ) && (
                  <li
                    className={combineClasses(
                      styles.requirementItem,
                      styles.unfulfilled
                    )}
                    style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                  >
                    Algunos evaluadores tienen información incompleta (nombre e
                    institución son requeridos)
                  </li>
                )}

              {/* Conflicto de Interés */}
              <li
                className={combineClasses(
                  styles.requirementItem,
                  formData.hasConflictOfInterest &&
                    (formData.hasConflictOfInterest === 'NO' ||
                      (formData.hasConflictOfInterest === 'SI' &&
                        formData.conflictOfInterestList?.length > 0 &&
                        formData.conflictOfInterestList.every(
                          (conflict: any) =>
                            conflict.name && conflict.institution
                        )))
                    ? styles.fulfilled
                    : styles.unfulfilled
                )}
              >
                Declaración de conflicto de interés
              </li>
              {!formData.hasConflictOfInterest && (
                <li
                  className={combineClasses(
                    styles.requirementItem,
                    styles.unfulfilled
                  )}
                  style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                >
                  Debe indicar si existe o no conflicto de interés
                </li>
              )}
              {formData.hasConflictOfInterest === 'SI' &&
                formData.conflictOfInterestList &&
                formData.conflictOfInterestList.length > 0 &&
                formData.conflictOfInterestList.every(
                  (conflict: any) => conflict.name && conflict.institution
                ) && (
                  <li
                    className={combineClasses(
                      styles.requirementItem,
                      styles.fulfilled
                    )}
                    style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                  >
                    {formData.conflictOfInterestList.length} conflicto(s)
                    declarado(s) correctamente
                  </li>
                )}
              {formData.hasConflictOfInterest === 'SI' &&
                formData.conflictOfInterestList &&
                formData.conflictOfInterestList.length > 0 &&
                !formData.conflictOfInterestList.every(
                  (conflict: any) => conflict.name && conflict.institution
                ) && (
                  <li
                    className={combineClasses(
                      styles.requirementItem,
                      styles.unfulfilled
                    )}
                    style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                  >
                    Algunos conflictos tienen información incompleta (nombre e
                    institución son requeridos)
                  </li>
                )}
              {formData.hasConflictOfInterest === 'SI' &&
                (!formData.conflictOfInterestList ||
                  formData.conflictOfInterestList.length === 0) && (
                  <li
                    className={combineClasses(
                      styles.requirementItem,
                      styles.unfulfilled
                    )}
                    style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                  >
                    Debe agregar al menos un conflicto de interés o cambiar la
                    respuesta a "NO"
                  </li>
                )}
              {formData.hasConflictOfInterest === 'NO' && (
                <li
                  className={combineClasses(
                    styles.requirementItem,
                    styles.fulfilled
                  )}
                  style={{ marginLeft: '1rem', fontSize: '0.9rem' }}
                >
                  Sin conflictos de interés
                </li>
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* BLOQUE 1 — DECLARACIÓN DE INCOMPATIBILIDADES */}
      <div
        className={combineClasses(
          styles.complianceSection,
          styles.reviewCard,
          (!!formData.finalReportsUC ||
            !!formData.finalReportsANID ||
            !!formData.duplicateFunding ||
            (!!formData.otherIncompatibilities && !!formData.otherIncompatibilitiesDetails) ||
            !!formData.noIncompatibilities)
            ? styles.complete
            : styles.incomplete
        )}
      >
        <h4 className={styles.declarationSubtitle}>
          Declaración de Incompatibilidades
        </h4>
        <p className={styles.declarationNote}>
          Revise las incompatibilidades en las Bases del concurso
        </p>

        <label className={styles.checkboxContainer}>
          <input type="checkbox" {...register('finalReportsUC')} disabled={readOnly} />
          <span className={styles.checkboxText}>
            Incompatibilidad: informes finales vencidos UC
          </span>
        </label>

        <label className={styles.checkboxContainer}>
          <input type="checkbox" {...register('finalReportsANID')} disabled={readOnly} />
          <span className={styles.checkboxText}>
            Incompatibilidad: informes finales vencidos ANID
          </span>
        </label>

        <label className={styles.checkboxContainer}>
          <input type="checkbox" {...register('duplicateFunding')} disabled={readOnly} />
          <span className={styles.checkboxText}>
            Incompatibilidad: la propuesta postulada ya cuenta con
            financiamiento para los mismos objetivos/actividades
          </span>
        </label>

        <div className={styles.otherIncompatibilities}>
          <label className={styles.checkboxContainer}>
            <input type="checkbox" {...register('otherIncompatibilities')} disabled={readOnly} />
            <span className={styles.checkboxText}>Otras incompatibilidades</span>
          </label>
          {watch('otherIncompatibilities') && (
            <input
              type="text"
              className={combineClasses(styles.textInput, readOnly && styles.readOnlyField)}
              {...register('otherIncompatibilitiesDetails')}
              placeholder="Especifique las otras incompatibilidades"
              readOnly={readOnly}
              style={{ marginTop: '0', marginLeft: '2rem' }}
            />
          )}
        </div>

        <label className={styles.checkboxContainer}>
          <input type="checkbox" {...register('noIncompatibilities')} disabled={readOnly} />
          <span className={styles.checkboxText}>No tengo incompatibilidades</span>
        </label>
      </div>

      {/* BLOQUE 2 — DECLARACIÓN DE VERACIDAD */}
      <div
        className={combineClasses(
          styles.complianceSection,
          styles.reviewCard,
          !!formData.informationDeclaration ? styles.complete : styles.incomplete
        )}
      >
        <h4 className={styles.declarationSubtitle}>Declaración de Veracidad</h4>

        <label className={`${styles.checkboxContainer} ${styles.checkboxContainerSpaced}`}>
          <input
            type="checkbox"
            {...register('informationDeclaration', {
              required: 'Debe confirmar la declaración de información',
            })}
            disabled={readOnly}
          />
          <span className={styles.checkboxText}>
            Declaro y certifico que toda la información y documentación que
            respalda esta solicitud es veraz, auténtica y completa.
          </span>
        </label>

        {errors.informationDeclaration && (
          <span className={styles.errorMessage}>
            {errors.informationDeclaration.message}
          </span>
        )}
      </div>

      {/* BLOQUE 3 — DECLARACIÓN DE SINGAPUR */}
      <div
        className={combineClasses(
          styles.complianceSection,
          styles.reviewCard,
          !!formData.singaporeDeclaration ? styles.complete : styles.incomplete
        )}
      >
        <div className={styles.declarationSubtitleRow}>
          <h4 className={styles.declarationSubtitle}>
            Declaración de Singapur sobre Integridad en la Investigación
          </h4>
          {!readOnly && (
            <Button
              type="button"
              variant={watch('singaporeDeclaration') ? 'secondary' : 'primary'}
              onClick={() => setSingaporeOpen(true)}
              className={styles.singaporeActionBtn}
            >
              {watch('singaporeDeclaration') ? 'Ver declaración' : 'Leer y aceptar'}
              {/* El kit pinta .uc-icon en azul; en variant="primary" (fondo azul)
                  quedaría invisible — se sobreescribe por estado. */}
              <i
                className="uc-icon icon-size--sm"
                style={{
                  color: watch('singaporeDeclaration')
                    ? 'var(--color-primary)'
                    : 'var(--color-white)',
                }}
              >
                description
              </i>
            </Button>
          )}
        </div>

        {watch('singaporeDeclaration') ? (
          <div className={styles.singaporeStatusAccepted}>
            <i className="uc-icon icon-size--sm">check_circle</i>
            Aceptada
          </div>
        ) : (
          <div className={styles.singaporeStatusPending}>
            Pendiente de lectura y aceptación
          </div>
        )}

        <input
          type="checkbox"
          style={{ display: 'none' }}
          {...register('singaporeDeclaration', {
            required: 'Debe suscribir la Declaración de Singapur',
          })}
        />
        {errors.singaporeDeclaration && !watch('singaporeDeclaration') && (
          <span className={styles.errorMessage}>
            {errors.singaporeDeclaration.message}
          </span>
        )}
      </div>

      {/* Display validation errors */}
      {validationErrors.length > 0 && (
        <ValidationErrorDisplay
          errors={validationErrors}
          onClose={clearValidationErrors}
        />
      )}

      {!readOnly &&
        (!isActive ? (
          <Alert variant="error">
            El concurso está cerrado; ya no se pueden realizar postulaciones.
          </Alert>
        ) : allSectionsComplete &&
          formData.informationDeclaration &&
          formData.singaporeDeclaration &&
          validationErrors.length === 0 ? (
          <Alert variant="success">
            Su postulación está lista para envío. Una vez enviada, no podrá
            realizar modificaciones. Revise cuidadosamente antes de proceder.
          </Alert>
        ) : (
          validationErrors.length === 0 && (
            <Alert variant="warning">
              Postulación incompleta. Complete todos los requisitos y confirme
              las declaraciones para habilitar el envío.
            </Alert>
          )
        ))}
    </div>
    {singaporeOpen && (
      <SingaporeDeclarationModal
        accepted={!!watch('singaporeDeclaration')}
        onAccept={(v) => setValue('singaporeDeclaration', v)}
        onClose={() => setSingaporeOpen(false)}
      />
    )}
    </>
  );
};

export default ReviewSubmission;
